import React, { useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Shield, Sparkles, X } from "lucide-react";
import { createPortal } from "react-dom";
import { useAuth } from "../../context/AuthContext";
import { countryCodes } from "../../data/countryCodes";
import { useFormik } from "formik";
import * as Yup from "yup";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db, functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";

/* ================= OVERLAY ================= */

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100dvh;
  background:
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.12), transparent 22%),
    radial-gradient(circle at top right, rgba(56, 189, 248, 0.12), transparent 22%),
    rgba(2, 6, 23, 0.78);
  backdrop-filter: blur(14px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow: hidden;
`;

/* ================= MODAL ================= */

const Modal = styled(motion.div)`
  width: 100%;
  max-width: 960px;
  margin: auto;
  flex-shrink: 0;
  max-height: min(90vh, 760px);
  height: min(90vh, 760px);
  overflow: hidden;

  background:
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.12), transparent 32%),
    radial-gradient(circle at top right, rgba(56, 189, 248, 0.12), transparent 34%),
    linear-gradient(180deg, rgba(8, 16, 29, 0.98), rgba(5, 11, 22, 0.98));
  border-radius: 28px;
  padding: 34px 28px 28px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 40px 120px rgba(0, 0, 0, 0.52);
  position: relative;

  /* smooth scrolling */
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.4) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.35);
    border-radius: 999px;
  }
`;

const ModalGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 0.92fr) minmax(0, 1fr);
  height: 100%;
  min-height: 0;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

const SidePanel = styled.div`
  position: relative;
  min-height: 0;
  padding: 34px 28px 28px;
  border-right: 1px solid rgba(148, 163, 184, 0.12);
  background:
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.14), transparent 36%),
    radial-gradient(circle at bottom right, rgba(56, 189, 248, 0.14), transparent 40%),
    linear-gradient(180deg, rgba(10, 17, 30, 0.96), rgba(6, 12, 24, 0.94));
  overflow: hidden;

  @media (max-width: 820px) {
    display: none;
  }
`;

const SideGlow = styled.div`
  position: absolute;
  border-radius: 999px;
  filter: blur(18px);
  pointer-events: none;
  opacity: 0.5;
`;

const SideEyebrow = styled.div`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.16);
  color: #7dd3fc;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const SideTitle = styled.h3`
  position: relative;
  z-index: 1;
  margin: 18px 0 10px;
  color: #f8fafc;
  font-size: 2.2rem;
  line-height: 1.05;
`;

const SideText = styled.p`
  position: relative;
  z-index: 1;
  margin: 0;
  color: #94a3b8;
  font-size: 14px;
  line-height: 1.85;
`;

const SideStack = styled.div`
  position: relative;
  z-index: 1;
  margin-top: 26px;
  display: grid;
  gap: 14px;
`;

const SideCard = styled.div`
  padding: 16px 16px 15px;
  border-radius: 20px;
  background: rgba(15, 23, 42, 0.54);
  border: 1px solid rgba(148, 163, 184, 0.12);

  strong {
    display: block;
    color: #f8fafc;
    font-size: 15px;
  }

  p {
    margin: 8px 0 0;
    color: #94a3b8;
    font-size: 13px;
    line-height: 1.7;
  }
`;

const FormPane = styled.div`
  position: relative;
  min-height: 0;
  padding: 34px 28px 28px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.4) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.35);
    border-radius: 999px;
  }

  @media (max-width: 820px) {
    min-height: auto;
    padding: 28px 20px 22px;
  }
`;

const Tabs = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin: 18px 0 20px;
  padding: 6px;
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.58);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const Tab = styled.button`
  min-height: 42px;
  border-radius: 14px;
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(245, 158, 11, 0.24)" : "transparent"};
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(135deg, rgba(245, 158, 11, 0.18), rgba(56, 189, 248, 0.14))"
      : "transparent"};
  color: ${({ $active }) => ($active ? "#f8fafc" : "#94a3b8")};
  font-size: 14px;
  font-weight: 900;
  cursor: pointer;
`;

/* ================= HEADER ================= */

const Close = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  height: 40px;
  width: 40px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.64);
  border: 1px solid rgba(148, 163, 184, 0.12);
  color: #cbd5e1;
  cursor: pointer;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.16);
  color: #7dd3fc;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Title = styled.h3`
  margin: 14px 0 6px;
  font-size: 28px;
  font-weight: 900;
  line-height: 1.08;
  color: #f8fafc;
`;

const Sub = styled.p`
  font-size: 13px;
  line-height: 1.65;
  color: #94a3b8;
  margin-bottom: 14px;
`;

/* ================= GOOGLE ================= */

const GoogleBtn = styled.button`
  width: 100%;
  min-height: 52px;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(255, 255, 255, 0.94);
  color: #111827;
  font-weight: 800;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;

  span {
    font-size: 12px;
    background: rgba(34, 197, 94, 0.18);
    color: #166534;
    padding: 4px 9px;
    border-radius: 999px;
    font-weight: 900;
  }
`;

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.68 1.23 9.17 3.26l6.84-6.84C35.87 2.21 30.28 0 24 0 14.64 0 6.51 5.38 2.56 13.22l7.97 6.19C12.4 13.72 17.77 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.5 24c0-1.57-.14-3.08-.4-4.54H24v9.59h12.65c-.55 2.96-2.19 5.47-4.65 7.18l7.17 5.57C43.73 37.65 46.5 31.36 46.5 24z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.41a14.5 14.5 0 0 1 0-8.82l-7.97-6.19A23.93 23.93 0 0 0 0 24c0 3.84.92 7.48 2.56 10.6l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.28 0 11.87-2.07 15.82-5.62l-7.17-5.57c-2 1.34-4.56 2.13-8.65 2.13-6.23 0-11.6-4.22-13.47-9.91l-7.97 6.19C6.51 42.62 14.64 48 24 48z"
    />
  </svg>
);

/* ================= DIVIDER ================= */

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 14px 0;
  color: #64748b;
  font-size: 13px;

  &:before,
  &:after {
    content: "";
    flex: 1;
    height: 1px;
    background: rgba(148, 163, 184, 0.14);
  }
`;

const FormGrid = styled.div`
  display: grid;
  gap: 10px;
`;

const ModePanel = styled(motion.div)`
  will-change: transform, opacity;
`;

const InputWrap = styled.div`
  margin-bottom: 2px;
`;

/* ================= FORM ================= */

const Input = styled.input`
  width: 100%;
  height: 50px;
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.14);
  padding: 0 14px;
  color: #f8fafc;
  font-size: 15px;

  &::placeholder {
    color: #7c8aa0;
  }
`;

const Forgot = styled.button`
  background: none;
  border: none;
  color: #60a5fa;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  margin-bottom: 12px;
  float: right;
`;

const Submit = styled.button`
  width: 100%;
  height: 50px;
  border-radius: 16px;
  background: linear-gradient(135deg, #f59e0b 0%, #f97316 38%, #38bdf8 100%);
  border: none;
  color: #07111f;
  font-weight: 900;
  font-size: 16px;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

/* ================= FOOTER ================= */

const Trust = styled.p`
  font-size: 12.5px;
  color: #94a3b8;
  margin-top: 12px;
  text-align: center;
`;
const PhoneRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
`;
const CountryCode = styled.select`
  height: 50px;
  border-radius: 16px;
  width: 100px;
  background: rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.14);
  color: #f8fafc;
  padding: 0 10px;
  font-size: 14px;
`;
const ErrorText = styled.p`
  font-size: 12px;
  color: #f87171;
  margin: -6px 0 8px;
`;

/* ================= COMPONENT ================= */
const loginSchema = Yup.object({
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string()
    .min(6, "Minimum 6 characters")
    .required("Password is required"),
});

const signupSchema = Yup.object({
  name: Yup.string().min(2, "Too short").required("Full name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string()
    .min(6, "Minimum 6 characters")
    .required("Password is required"),
  phone: Yup.string()
    .matches(/^[0-9]{7,15}$/, "Invalid phone number")
    .required("Mobile number is required"),
  countryCode: Yup.string().required(),
});

export default function AuthModal({
  open,
  onClose,
  onSuccess,
  initialMode = "login",
}) {
  const { login, signup, loginWithGoogle, resetPassword } = useAuth();

  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (open) {
      setMode(initialMode);
    }
  }, [initialMode, open]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      email: "",
      password: "",
      phone: "",
      countryCode: "+91",
    },
    validationSchema: mode === "login" ? loginSchema : signupSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);

        if (mode === "login") {
          await login(values.email, values.password);
        } else {
          const fullPhone = `${values.countryCode}${values.phone}`;

          // 1️⃣ Create auth user
          const userCredential = await signup(values.email, values.password);

          const user = userCredential.user;

          // 2️⃣ Save extra data in Firestore
          await setDoc(doc(db, "users", user.uid), {
            uid: user.uid,
            email: values.email,
            name: values.name, // ✅ FIXED
            phone: fullPhone, // ✅ FIXED
            provider: "password",
            role: "user",
            onboardingCompleted: false,
            photo: user.photoURL || "",
            createdAt: serverTimestamp(),
          });
          httpsCallable(
            functions,
            "sendWelcomeEmail"
          )({
            email: values.email,
            name: values.name,
          });
        }

        onSuccess?.(mode);
      } catch (e) {
        alert(e.message);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleGoogle = async () => {
    try {
      setLoading(true);
      await loginWithGoogle();
      onSuccess?.(mode);
    } catch (e) {
      if (e.code !== "auth/cancelled-popup-request") {
        alert(e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // const handleReset = async () => {
  //   if (!email) return alert("Enter email first");
  //   await resetPassword(email);
  //   alert("Password reset email sent");
  // };
  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Modal
            initial={{ scale: 0.94, opacity: 0, y: 18 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 12 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            isSignup={mode === "signup"}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalGrid>
              <SidePanel>
                <SideGlow
                  style={{
                    width: 220,
                    height: 220,
                    top: -40,
                    left: -60,
                    background: "rgba(245, 158, 11, 0.24)",
                  }}
                />
                <SideGlow
                  style={{
                    width: 260,
                    height: 260,
                    right: -90,
                    bottom: -70,
                    background: "rgba(56, 189, 248, 0.2)",
                  }}
                />
                <SideEyebrow>
                  <Sparkles size={13} />
                  {mode === "login"
                    ? "Resume your progress"
                    : "Career clarity starts here"}
                </SideEyebrow>
                <SideTitle>
                  {mode === "login"
                    ? "Get back to your roadmap quickly."
                    : "Create your free account and start with direction."}
                </SideTitle>
                <SideText>
                  {mode === "login"
                    ? "Pick up your roadmap, dashboard, and daily execution without losing momentum."
                    : "Turn confusion into a clear roadmap, daily tasks, and better career consistency."}
                </SideText>

                <SideStack>
                  <SideCard>
                    <strong>Personalized roadmap</strong>
                    <p>
                      Get a structured phase-to-week-to-day system instead of
                      random learning.
                    </p>
                  </SideCard>
                  <SideCard>
                    <strong>Daily execution support</strong>
                    <p>
                      Track progress, stay consistent, and know what matters
                      next.
                    </p>
                  </SideCard>
                  <SideCard>
                    <strong>Built for real learners</strong>
                    <p>
                      No noise, no hype. Just clarity, momentum, and practical
                      action.
                    </p>
                  </SideCard>
                </SideStack>
              </SidePanel>

              <FormPane>
                <Close onClick={onClose}>
                  <X size={18} />
                </Close>

                <Eyebrow>
                  <Sparkles size={13} />
                  {mode === "login"
                    ? "Resume your progress"
                    : "Create your free account"}
                </Eyebrow>

                {mode === "login" && (
                  <>
                    <Title>Welcome back 👋</Title>
                    <Sub>
                      Continue your roadmap, daily tasks and progress tracking.
                    </Sub>
                  </>
                )}

                <Tabs>
                  <Tab $active={mode === "login"} onClick={() => setMode("login")}>
                    Login
                  </Tab>
                  <Tab $active={mode === "signup"} onClick={() => setMode("signup")}>
                    Sign up
                  </Tab>
                </Tabs>

                <AnimatePresence mode="wait" initial={false}>
                  <ModePanel
                    key={mode}
                    initial={{ opacity: 0, x: mode === "login" ? -28 : 28 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: mode === "login" ? 28 : -28 }}
                    transition={{ duration: 0.22, ease: "easeOut" }}
                  >
                    <GoogleBtn onClick={handleGoogle}>
                      <GoogleIcon />
                      Continue with Google <span>Recommended</span>
                    </GoogleBtn>

                    <Divider>or</Divider>
                    <FormGrid>
                      {mode === "signup" && (
                        <>
                          <InputWrap>
                            <Input
                              placeholder="Full name"
                              name="name"
                              value={formik.values.name}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            />
                            {formik.touched.name && formik.errors.name && (
                              <ErrorText>{formik.errors.name}</ErrorText>
                            )}
                          </InputWrap>

                          <InputWrap>
                            <PhoneRow>
                              <CountryCode
                                name="countryCode"
                                value={formik.values.countryCode}
                                onChange={formik.handleChange}
                              >
                                {countryCodes.map((c) => (
                                  <option key={c.code + c.label} value={c.code}>
                                    {c.flag} {c.code}
                                  </option>
                                ))}
                              </CountryCode>

                              <Input
                                placeholder="Mobile number"
                                name="phone"
                                value={formik.values.phone}
                                onChange={(e) =>
                                  formik.setFieldValue(
                                    "phone",
                                    e.target.value.replace(/\D/g, "")
                                  )
                                }
                                onBlur={formik.handleBlur}
                              />
                            </PhoneRow>
                            {formik.touched.phone && formik.errors.phone && (
                              <ErrorText>{formik.errors.phone}</ErrorText>
                            )}
                          </InputWrap>
                        </>
                      )}

                      <InputWrap>
                        <Input
                          placeholder="Email"
                          name="email"
                          value={formik.values.email}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.email && formik.errors.email && (
                          <ErrorText>{formik.errors.email}</ErrorText>
                        )}
                      </InputWrap>

                      <InputWrap>
                        <Input
                          placeholder="Password"
                          type="password"
                          name="password"
                          value={formik.values.password}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.password && formik.errors.password && (
                          <ErrorText>{formik.errors.password}</ErrorText>
                        )}
                      </InputWrap>
                    </FormGrid>

                    {mode === "login" && (
                      <Forgot
                        onClick={() => {
                          if (!formik.values.email) {
                            return alert("Enter email first");
                          }
                          resetPassword(formik.values.email);
                          alert("Password reset email sent");
                        }}
                      >
                        Forgot password?
                      </Forgot>
                    )}

                    <Submit onClick={formik.handleSubmit} disabled={loading}>
                      {loading
                        ? "Please wait..."
                        : mode === "login"
                        ? "Login"
                        : "Create account"}
                      {!loading && <ArrowRight size={16} />}
                    </Submit>

                    <Trust>
                      Private. Secure. No spam. Built for steady developer progress.
                    </Trust>
                  </ModePanel>
                </AnimatePresence>
              </FormPane>
            </ModalGrid>
          </Modal>
        </Overlay>
      )}
    </AnimatePresence>,
    document.body
  );
}
